/** 分析栏最近搜索（本机 localStorage，不按账号同步） */

export type RecentSearchItem = {
  name: string;
  creditCode?: string;
  /** chip 短文案；缺省由 name 推导 */
  label?: string;
};

const STORAGE_KEY = 'tax-insight:recent-company-searches';
/** 本地最多保留条数 */
export const RECENT_SEARCH_STORE_CAP = 10;
/** 分析栏默认展示条数 */
export const RECENT_SEARCH_DISPLAY_LIMIT = 5;

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

/** chip 短名：去掉常见公司后缀，过长再截断 */
export function shortCompanyLabel(name: string): string {
  const raw = name.trim();
  if (!raw) return '';
  const stripped = raw
    .replace(/(股份有限公司|有限责任公司|有限公司|集团有限公司)$/u, '')
    .trim();
  const base = stripped || raw;
  return base.length > 10 ? `${base.slice(0, 10)}…` : base;
}

function normalizeItem(raw: unknown): RecentSearchItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  if (!name) return null;
  const creditCode =
    typeof o.creditCode === 'string' && o.creditCode.trim()
      ? o.creditCode.trim()
      : undefined;
  const label =
    typeof o.label === 'string' && o.label.trim()
      ? o.label.trim()
      : shortCompanyLabel(name);
  return { name, creditCode, label };
}

function readAll(): RecentSearchItem[] {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: RecentSearchItem[] = [];
    const seen = new Set<string>();
    for (const item of parsed) {
      const n = normalizeItem(item);
      if (!n) continue;
      const key = n.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(n);
      if (out.length >= RECENT_SEARCH_STORE_CAP) break;
    }
    return out;
  } catch {
    return [];
  }
}

function writeAll(items: RecentSearchItem[]): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(items.slice(0, RECENT_SEARCH_STORE_CAP)),
    );
  } catch {
    /* quota / private mode */
  }
}

/** 读取最近搜索（按最近使用优先） */
export function listRecentSearches(
  limit: number = RECENT_SEARCH_DISPLAY_LIMIT,
): RecentSearchItem[] {
  const n = Math.max(0, Math.min(limit, RECENT_SEARCH_STORE_CAP));
  return readAll().slice(0, n);
}

/**
 * 输入联想：从本机历史中匹配名称 / 信用代码 / 短名。
 * 空关键词时返回最近若干条，便于聚焦输入框即提示。
 */
export function suggestRecentSearches(
  query: string,
  limit: number = RECENT_SEARCH_DISPLAY_LIMIT,
): RecentSearchItem[] {
  const n = Math.max(0, Math.min(limit, RECENT_SEARCH_STORE_CAP));
  const q = query.trim().toLowerCase();
  const all = readAll();
  if (!q) return all.slice(0, n);
  const matched: RecentSearchItem[] = [];
  for (const item of all) {
    const hay = [item.name, item.creditCode || '', item.label || '']
      .join(' ')
      .toLowerCase();
    if (!hay.includes(q)) continue;
    matched.push(item);
    if (matched.length >= n) break;
  }
  return matched;
}

/**
 * 栏内补全：取第一条「名称以前缀匹配」的历史（区分大小写无关）。
 * 返回命中项与待显示的后缀文案（相对当前输入长度截取自正式名称）。
 */
export function prefixMatchRecentSearch(
  query: string,
): { item: RecentSearchItem; suffix: string } | null {
  const q = query.trim();
  if (!q) return null;
  const lower = q.toLowerCase();
  for (const item of readAll()) {
    if (item.name.toLowerCase().startsWith(lower) && item.name.length > q.length) {
      return { item, suffix: item.name.slice(q.length) };
    }
    const code = (item.creditCode || '').trim();
    if (code && code.toLowerCase().startsWith(lower) && code.length > q.length) {
      return { item, suffix: code.slice(q.length) };
    }
  }
  return null;
}

/**
 * 写入一条最近搜索（同名顶到最前；超出 STORE_CAP 截断）。
 * 返回写入后的完整列表（最多 STORE_CAP）。
 */
export function pushRecentSearch(input: {
  name: string;
  creditCode?: string;
  label?: string;
}): RecentSearchItem[] {
  const name = input.name.trim();
  if (!name) return readAll();
  const next: RecentSearchItem = {
    name,
    creditCode: input.creditCode?.trim() || undefined,
    label: (input.label || shortCompanyLabel(name)).trim() || shortCompanyLabel(name),
  };
  const rest = readAll().filter((x) => x.name.toLowerCase() !== name.toLowerCase());
  const list = [next, ...rest].slice(0, RECENT_SEARCH_STORE_CAP);
  writeAll(list);
  return list;
}
